import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorBouquetInStock } from "../../api/shopMode";
import type { DefaultUom } from "../../api/types";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { bouquetCostFigure } from "./kioskCostDisplay";
import { useT } from "../../i18n/LanguageProvider";
import { eur, uomLabel } from "./shopFloorFormat";
import { dateText } from "../../i18n/intl";

/**
 * G-85: this file used to carry its own `eur`, `uomSuffix` and a `qtyLabel`
 * that pluralised by sticking an "s" on the end. That could not survive
 * translation — "стебельs" — and it was already a second copy of
 * `shopFloorFormat`. One implementation, and the plural comes from
 * `Intl.PluralRules`.
 */
function qtyParts(quantity: string, uom: DefaultUom) {
  const n = Number(quantity);
  return {
    qty: Number.isFinite(n) ? String(n) : quantity,
    unit: uomLabel(uom, Number.isFinite(n) ? n : undefined),
  };
}

/**
 * Hub "Bouquets" view: the FINALIZED bouquets currently in stock at this
 * location. Each card shows the photo (template image when available), name,
 * who made it and the cost to build; tapping a card expands the exact items
 * that went into it.
 */
export function ShopFloorBouquetStockPanel() {
  const { t } = useT();
  const [bouquets, setBouquets] = useState<FloorBouquetInStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setBouquets(await shopModeApi.floorListBouquetsInStock());
    } catch (e) {
      setError(
        errorText(e, "bouquetStock.couldNotLoad"),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  return (
    <div style={{ display: "grid", gap: "var(--space-3)", alignContent: "start" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "var(--space-2)",
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>{t("hub.bouquetsInStock")}</h2>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {loading ? (
        <p className="muted" style={{ margin: 0 }}>{t("common.loading")}</p>
      ) : bouquets.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t("bouquetStock.none")}
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(16rem, 1fr))",
            gap: "var(--space-3)",
            alignItems: "start",
          }}
        >
          {bouquets.map((b) => {
            const expanded = expandedId === b.id;
            // WO-B (OD-198): with a component of unknown cost, the figure is
            // the known part, labelled so, with the count beside it.
            const buildCost = bouquetCostFigure(b);
            return (
              <button
                key={b.id}
                type="button"
                className="card"
                onClick={() => setExpandedId(expanded ? null : b.id)}
                aria-expanded={expanded}
                style={{
                  padding: "var(--space-3)",
                  display: "grid",
                  gap: "var(--space-2)",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div
                  className="sf-bouquet-template-thumb"
                  style={{ aspectRatio: "4 / 3", overflow: "hidden", borderRadius: "var(--radius, 8px)" }}
                >
                  <ShopFloorBouquetThumb
                    id={b.templateId ?? b.id}
                    imageKey={b.templateId ? b.templateImageKey : null}
                    alt={b.name || b.templateName || t("sale.bouquet")}
                    kind="template"
                  />
                </div>
                <div>
                  <strong>{b.name || b.templateName || t("sale.bouquet")}</strong>
                  {b.templateName && b.name && b.templateName !== b.name ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {t("bouquetStock.fromTemplate", { name: b.templateName })}
                    </p>
                  ) : null}
                  {/* OD-31: physically in the shop, but spoken for — the
                      stock view must tell the same story as the till. */}
                  {b.forOrderCode != null ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {t("sale.forOrder", { code: b.forOrderCode })}
                    </p>
                  ) : b.forShopify ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {t("sale.forShopify")}
                    </p>
                  ) : null}
                </div>
                <p className="muted" style={{ margin: 0 }}>
                  {t("bouquetStock.madeBy", {
                    who: b.builtByName,
                    when: dateText(new Date(b.createdAt), {
                      month: "short",
                      day: "numeric",
                    }),
                  })}
                </p>
                <p style={{ margin: 0 }}>
                  {buildCost.partial
                    ? t("bouquetStock.knownCost")
                    : t("bouquetStock.costToBuild")}{" "}
                  <strong>{buildCost.amount}</strong>
                  {buildCost.pill ? (
                    <>
                      {" "}
                      <span className="badge warning">
                        {buildCost.pill}
                      </span>
                    </>
                  ) : null}
                </p>
                <p style={{ margin: 0 }}>
                  {t("bouquetStock.suggestedPrice")}{" "}
                  <strong>
                    {b.suggestedSalePriceEur != null
                      ? eur(Number(b.suggestedSalePriceEur))
                      : "—"}
                  </strong>
                </p>
                {expanded ? (
                  <div style={{ display: "grid", gap: "var(--space-1)" }}>
                    <strong>{t("bouquetStock.whatsInside")}</strong>
                    {b.lines.length === 0 ? (
                      <p className="muted" style={{ margin: 0 }}>
                        {t("bouquetStock.noLines")}
                      </p>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {b.lines.map((line) => (
                          <li key={line.id}>
                            {t("bouquetStock.linePart", {
                              item: line.itemName,
                              ...qtyParts(line.quantity, line.uom),
                            })}
                          </li>
                        ))}
                      </ul>
                    )}
                    {b.notes ? (
                      <p className="muted" style={{ margin: 0 }}>
                        {t("bouquetStock.notePrefix", { note: b.notes })}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted" style={{ margin: 0 }}>
                    {t("bouquetStock.tapToSee")}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
